
import { computed, defineComponent, onMounted, onUnmounted, PropType, reactive, ref, toRefs, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useReflectiveInjector } from '@tanbo/vue-di-plugin'
import {
  getColorDepthLabels,
  ConnectStateRes,
  parseConnectInfo,
  IS_HTTP, isClientSupportVnc,
  NO_VIDEO_DECODER,
  REMOTE_STATE,
  REMOTE_TYPE,
  REMOTE_TYPE_CARD_META,
  RemoteAssistService, OS_REMOTE, RemoteTypeCard, getCardDisableText, NO_SUBTLE, VncDevice
} from '@/views/dashboard/group/client/modals/remote-assist/remote-assist.service'
import { timer } from 'rxjs'
import { getSvgIconDataUrl } from '@/utils/svg-icon-data-url'
import DetailsSubscribeBar from '@/views/dashboard/_components/subscribe-bar/details-subscribe-bar.vue'
import { getAuthByList, PermissionFe } from '@/services/service-permission.service'
import { TerminalAuthStatus } from '@/views/dashboard/_enum/terminal-auth-status'
import { ClientItem } from '@/views/dashboard/_type/client-item'
import { AreaType } from '@/views/dashboard/service/_enum/area-type'
import { BrandConfig, useBrandCustomStore } from '@/services/brand-custom.service'
import { REMOTEASSISTSETUP } from '@/config/external-link'
import { TerminalType, TerminalTypeLabel } from '@/views/dashboard/_enum/terminal-type'
import VncPassword from '@/views/dashboard/group/client/modals/remote-assist/vnc-password/vnc-password.vue'
import { User, UserInfo } from '@/services/user'
import { VncPdType } from '@/services/login.service'
import { passwordHmac } from '@/utils/format'
import { sshDeviceStore, vncDeviceStore } from '@/services/remote-device.service'
import { IS_JP_ONLY_BUILD } from '@/common/const/build-locale'

const ModulePermissionKey = {
  [AreaType.normal]: PermissionFe.remote_control,
  [AreaType.linux]: PermissionFe.linux_base_auth,
}

const DEFAULT_ERROR_MAP = {
  popupBlocked: false,
  needHttps: false,
  browserVersion: false,
}

export default defineComponent({
  components: { VncPassword, DetailsSubscribeBar },
  props: {
    terminalInfo: {
      type: Object as PropType<ClientItem>,
      default: () => ({})
    },
  },
  emits: ['close', 'authSuccess'],
  setup (props, context) {
    const { t, locale } = useI18n()
    const injector = useReflectiveInjector([RemoteAssistService])
    const userService = injector.get(User)
    const service = injector.get(RemoteAssistService)
    const userInfo = reactive<Partial<UserInfo>>({})
    const deviceInfo = ref({} as ClientItem)
    const errorMap = reactive({ ...DEFAULT_ERROR_MAP })
    const isRemoteWinClosing = ref(false) // 外部远控窗口(ssh、vnc)断开中，跟本窗口不在同一个窗口
    let remoteWinCloseInterval = setInterval(storageListener, 500)
    const data = reactive({
      modalVisible: true,
      state: REMOTE_STATE.UNINITIALIZED,
      applicationUrl: '',
      token: '',
      roomId: '',
      vncWsurl: '',
      assistType: REMOTE_TYPE.REMOTE_ASSIST,
      definitionSetting: false,
      login: {
        username: '',
        password: '',
        port: null,
        colorDepth: 32,
        enableTheming: true,
        enableWallpaper: true,
        enableFullWindowDrag: false
      },
      waitGetStatus: false,
      endLoading: false,
      statusTimer: null,
    })

    userService.userInfo.subscribe(info => { Object.assign(userInfo, info) })

    const hasPermission = computed(() => {
      return getAuthByList(deviceInfo.value.auth_status, ModulePermissionKey[props.terminalInfo.area_type]) === TerminalAuthStatus.normal
    })

    const disabledConnect = computed(() => {
      return !remoteTypeShow.value.find(v => !v.disableText)
        || (errorMap.needHttps || errorMap.browserVersion)
        || !hasPermission.value
        || isRemoteWinClosing.value
    })

    /** VNC远控or旧版远控 切换总开关 */
    const isUseVnc = computed(() => {
      if (data.assistType === REMOTE_TYPE.REMOTE_SSH) {
        return false
      }
      const isClientSupport = isClientSupportVnc(props.terminalInfo.version, props.terminalInfo.system_type)
      const config = OS_REMOTE[props.terminalInfo.system_type]
      return config.has_vnc && isClientSupport
    })

    const remoteTypeShow = computed(() => {
      const _localeDep = locale.value
      const cards = IS_JP_ONLY_BUILD
        ? REMOTE_TYPE_CARD_META.filter(c => c.type !== REMOTE_TYPE.REMOTE_SSH)
        : REMOTE_TYPE_CARD_META
      return cards.map((typeConfig) => {
        let title = ''
        let desc = ''
        if (typeConfig.type === REMOTE_TYPE.REMOTE_ASSIST) {
          title = t('groupClient.remoteAssistCardTitleAssist')
          desc = t('groupClient.remoteAssistCardDescAssist')
        } else if (typeConfig.type === REMOTE_TYPE.REMOTE_LOGIN) {
          title = t('groupClient.remoteAssistCardTitleLogin')
          desc = t('groupClient.remoteAssistCardDescLogin')
        } else if (typeConfig.type === REMOTE_TYPE.REMOTE_SSH) {
          title = t('groupClient.remoteAssistCardTitleSsh')
          desc = t('groupClient.remoteAssistCardDescSsh')
        }
        const disableText = getCardDisableText(typeConfig, props.terminalInfo, t)
        const icon = typeConfig.resolveSvg
          ? getSvgIconDataUrl(typeConfig.icon)
          : typeConfig.icon
        return { ...typeConfig, title, desc, disableText, icon }
      })
    })

    const colorDepthList = computed(() => {
      void locale.value
      return getColorDepthLabels(t)
    })
    const colorDepthKeys = computed(() => Object.keys(colorDepthList.value))

    function updateDeviceInfo () {
      service.getDeviceInfo({
        device_id: props.terminalInfo.device_id,
        permission_names: [ModulePermissionKey[props.terminalInfo.area_type]],
        group_id: -1,
      }).then(res => {
        deviceInfo.value = res.terminal_list?.[0] || {} as ClientItem
      })
    }

    function handleRemoteStatus (res: ConnectStateRes) {
      if (res.connect_status === REMOTE_STATE.READY) {
        if (data.state !== REMOTE_STATE.READY) {
          openWindow(res)
        }
      }
      if (res.connect_status === REMOTE_STATE.IDLE) {
        data.endLoading = false
      }
      data.state = res.connect_status
    }

    function openWindow (res: ConnectStateRes) {
      data.token = res.token
      data.login.port = res.remote_port
      if (isUseVnc.value || data.assistType === REMOTE_TYPE.REMOTE_SSH) {
        data.roomId = parseConnectInfo(res.ws_url).room_id
        data.vncWsurl = res.ws_url
        openWebWindow()
      } else if (data.assistType === REMOTE_TYPE.REMOTE_ASSIST) {
        data.applicationUrl = `kremoteassistclient:://-s:${res.frp_endpoint};-p:${res.connect_port};-t:${res.token};-sk:${res.sk};-i:${props.terminalInfo.device_id};-v:${res.visitor_version}`
        location.href = data.applicationUrl
      } else if (data.assistType === REMOTE_TYPE.REMOTE_LOGIN) {
        openWebWindow()
      }
    }

    function getClientStatus () {
      data.statusTimer = timer(0, 3000).subscribe(() => {
        if (data.waitGetStatus) {
          return
        }
        service.getConnectState({
          device_id: props.terminalInfo.device_id,
          remote_type: data.assistType
        }).then((res: ConnectStateRes) => {
          handleRemoteStatus(res)
        })
      })
    }

    function doWaitGetStatus () {
      data.waitGetStatus = true
      setTimeout(() => {
        data.waitGetStatus = false
      }, 1000)
    }

    function handleAuthSuccess () {
      updateDeviceInfo()
      context.emit('authSuccess')
    }

    function turnOffSshConnect () {
      data.endLoading = true
      service.turnOffSsh({ device_id: props.terminalInfo.device_id, token: data.token, room_id: data.roomId })
    }

    function turnOffConnect () {
      data.endLoading = true
      service.turnOffRemoteAssist({ device_id: props.terminalInfo.device_id, token: data.token, room_id: data.roomId })
    }

    function turnOffLoginConnect () {
      data.endLoading = true
      service.turnOffRemoteLogin({ device_id: props.terminalInfo.device_id, token: data.token, room_id: data.roomId })
    }

    function httpsRedirect () {
      window.location.href = window.location.href.replace('http:', 'https:')
    }

    function openWebWindow () {
      let newWindow
      if (isUseVnc.value) {
        newWindow = service.openVncWindow(data.vncWsurl)
      } else if (data.assistType === REMOTE_TYPE.REMOTE_SSH) {
        newWindow = service.openSshWindow(data.vncWsurl)
      } else {
        newWindow = service.openRemoteLoginWindow({ deviceId: props.terminalInfo.device_id, login: data.login })
      }
      errorMap.popupBlocked = !newWindow
    }

    function updateSameError () {
      errorMap.needHttps = (isUseVnc.value && IS_HTTP && NO_VIDEO_DECODER)
        || (data.assistType === REMOTE_TYPE.REMOTE_SSH && NO_SUBTLE)
      errorMap.browserVersion = isUseVnc.value && !IS_HTTP && NO_VIDEO_DECODER
    }

    function storageListener () {
      let deviceInfo = {} as VncDevice
      if (isUseVnc.value) {
        deviceInfo = vncDeviceStore.getDevice(props.terminalInfo.device_id)
      } else if (data.assistType === REMOTE_TYPE.REMOTE_SSH) {
        deviceInfo = sshDeviceStore.getDevice(props.terminalInfo.device_id)
      }
      isRemoteWinClosing.value = !!deviceInfo?.unload_timestamp
    }

    onMounted(() => {
      if (props.terminalInfo.device_id) {
        updateDeviceInfo()
      }
    })

    onUnmounted(() => {
      data.statusTimer?.unsubscribe()
      clearInterval(remoteWinCloseInterval)
    })

    getClientStatus()

    watch(isUseVnc, updateSameError, { immediate: true })

    watch(remoteTypeShow, (value) => {
      data.assistType = value.find(v => !v.disableText)?.type || REMOTE_TYPE.REMOTE_ASSIST
    }, { immediate: true })

    return {
      ...toRefs(data),
      IS_HTTP,
      NO_VIDEO_DECODER,
      VncPdType,
      TerminalType,
      REMOTEASSISTSETUP,
      brandConfig: useBrandCustomStore().brandConfig as BrandConfig,
      TerminalAuthStatus,
      AreaType,
      ModulePermissionKey,
      PermissionFe,
      deviceInfo,
      userInfo,
      errorMap,
      hasPermission,
      remoteTypeShow,
      colorDepthList,
      colorDepthKeys,
      disabledConnect,
      isUseVnc,
      REMOTE_STATE,
      REMOTE_TYPE,
      origin: window.location.origin,
      getAuthByList,
      handleAuthSuccess,
      turnOffConnect,
      httpsRedirect,
      openWebWindow,
      turnOffLoginConnect,
      turnOffSshConnect,
      selectAssistType (item: RemoteTypeCard) {
        if (item.disableText) return
        data.assistType = item.type
      },
      handleRemoteLoginConnect () {
        if ((!data.login.username.trim() && !isUseVnc.value) || !data.login.password.trim()) {
          return
        }

        if (data.waitGetStatus) {
          return
        }

        data.state = REMOTE_STATE.WAITING
        service.turnOnRemoteLogin({
          device_id: props.terminalInfo.device_id,
          password: passwordHmac(data.login.password),
        }).catch(() => {
          data.state = REMOTE_STATE.IDLE
        })
        doWaitGetStatus()
      },
      handleSshConnect () {
        if (!data.login.password.trim() || data.waitGetStatus) {
          return
        }

        data.state = REMOTE_STATE.WAITING
        service.turnOnSsh({
          device_id: props.terminalInfo.device_id,
          password: passwordHmac(data.login.password),
        }).catch(() => {
          data.state = REMOTE_STATE.IDLE
        })
        doWaitGetStatus()
      },
      handleDefinitionSetting () {
        data.definitionSetting = !data.definitionSetting
      },
      turnOnConnect () {
        data.state = REMOTE_STATE.WAITING
        service.turnOnRemoteAssist({
          device_id: props.terminalInfo.device_id
        }).catch(() => {
          data.state = REMOTE_STATE.IDLE
        })
      },
      handleClose () {
        if ([REMOTE_STATE.WAITING].includes(data.state)) {
          const turnOffMap = {
            [REMOTE_TYPE.REMOTE_LOGIN]: turnOffLoginConnect,
            [REMOTE_TYPE.REMOTE_ASSIST]: turnOffConnect,
            [REMOTE_TYPE.REMOTE_SSH]: turnOffSshConnect
          }
          turnOffMap[data.assistType]?.()
        }
        context.emit('close')
      }
    }
  }
})
